/**
 * 注册页面
 * 支持邮箱验证码注册
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authAPI } from '@/services/authApi';
import { Loader2, Mail, Gift, CheckCircle2, XCircle } from 'lucide-react';

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    emailCode: '',
    invitationCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailServiceEnabled, setEmailServiceEnabled] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const [codeExpireSeconds, setCodeExpireSeconds] = useState(60);
  const [invitationCodeStatus, setInvitationCodeStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [inviterUsername, setInviterUsername] = useState('');

  // 从 URL 参数中读取邀请码
  useEffect(() => {
    const invitationCodeFromUrl = searchParams.get('invitationCode');
    if (invitationCodeFromUrl) {
      setFormData(prev => ({
        ...prev,
        invitationCode: invitationCodeFromUrl.toUpperCase(),
      }));
    }
  }, [searchParams]);

  // 检查邮件服务状态
  useEffect(() => {
    authAPI.getEmailServiceStatus().then(res => {
      if (res.success && res.data) {
        setEmailServiceEnabled(res.data.enabled);
        setCodeExpireSeconds(res.data.codeExpireSeconds || 60);
      }
    }).catch(() => {
      // 如果获取失败，默认不启用邮箱验证
    });
  }, []);

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 邀请码验证（防抖）
  useEffect(() => {
    const code = formData.invitationCode.trim();
    if (!code) {
      setInvitationCodeStatus('idle');
      setInviterUsername('');
      return;
    }

    if (code.length < 4) {
      setInvitationCodeStatus('idle');
      return;
    }

    setInvitationCodeStatus('validating');
    const timer = setTimeout(async () => {
      try {
        const res = await authAPI.validateInvitationCode(code);
        if (res.success && res.data?.valid) {
          setInvitationCodeStatus('valid');
          setInviterUsername(res.data.inviterUsername || '');
        } else {
          setInvitationCodeStatus('invalid');
          setInviterUsername('');
        }
      } catch {
        setInvitationCodeStatus('invalid');
        setInviterUsername('');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.invitationCode]);

  const handleSendCode = useCallback(async () => {
    if (!formData.email) {
      setError('请先输入邮箱');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('邮箱格式不正确');
      return;
    }

    setError('');
    setSendingCode(true);

    try {
      await authAPI.sendRegisterCode(formData.email);
      setCodeSent(true);
      setCountdown(codeExpireSeconds);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '发送验证码失败';
      setError(errorMessage);
    } finally {
      setSendingCode(false);
    }
  }, [formData.email, codeExpireSeconds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 验证密码
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    if (formData.password.length < 8) {
      setError('密码长度至少为8位');
      return;
    }

    // 如果邮件服务启用，验证码必填
    if (emailServiceEnabled && !formData.emailCode) {
      setError('请输入邮箱验证码');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        emailCode: emailServiceEnabled ? formData.emailCode : undefined,
        invitationCode: formData.invitationCode.trim() || undefined,
      });
      
      if (response.success) {
        // 注册成功，跳转到登录页
        const successMessage = response.message || '注册成功，请登录';
        navigate('/login', { 
          state: { message: successMessage }
        });
      } else {
        setError(response.message || '注册失败');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '注册失败，请稍后重试';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="flex-1 flex items-center justify-center w-full max-w-md">
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">创建账号</CardTitle>
            <CardDescription className="text-center">
              注册 MCP Hub 账号
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="username">用户名</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={loading}
                  minLength={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">邮箱</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              {/* 邮箱验证码（仅在邮件服务启用时显示） */}
              {emailServiceEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="emailCode">邮箱验证码</Label>
                  <div className="flex gap-2">
                    <Input
                      id="emailCode"
                      type="text"
                      placeholder="请输入验证码"
                      value={formData.emailCode}
                      onChange={(e) => setFormData({ ...formData, emailCode: e.target.value })}
                      required
                      disabled={loading}
                      maxLength={6}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSendCode}
                      disabled={sendingCode || countdown > 0 || loading}
                      className="whitespace-nowrap min-w-[120px]"
                    >
                      {sendingCode ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : countdown > 0 ? (
                        `${countdown}秒后重发`
                      ) : codeSent ? (
                        '重新发送'
                      ) : (
                        <>
                          <Mail className="mr-1 h-4 w-4" />
                          获取验证码
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">密码</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="至少8位字符"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">确认密码</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="再次输入密码"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              {/* 邀请码（可选） */}
              <div className="space-y-2">
                <Label htmlFor="invitationCode" className="flex items-center gap-1">
                  <Gift className="h-4 w-4 text-orange-500" />
                  邀请码
                  <span className="text-xs text-muted-foreground">（选填）</span>
                </Label>
                <div className="relative">
                  <Input
                    id="invitationCode"
                    type="text"
                    placeholder="输入邀请码可获得额外奖励"
                    value={formData.invitationCode}
                    onChange={(e) => setFormData({ ...formData, invitationCode: e.target.value.toUpperCase() })}
                    disabled={loading}
                    maxLength={12}
                    className="pr-8"
                  />
                  {invitationCodeStatus === 'validating' && (
                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  {invitationCodeStatus === 'valid' && (
                    <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                  )}
                  {invitationCodeStatus === 'invalid' && (
                    <XCircle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                  )}
                </div>
                {invitationCodeStatus === 'valid' && inviterUsername && (
                  <p className="text-xs text-green-600 dark:text-green-400">
                    ✨ 邀请人：{inviterUsername}，注册后双方各获得 token 奖励
                  </p>
                )}
                {invitationCodeStatus === 'invalid' && (
                  <p className="text-xs text-red-500">
                    邀请码无效或已达使用上限
                  </p>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                注册
              </Button>

              <div className="text-sm text-center text-gray-600 dark:text-gray-400">
                已有账号？
                <Link to="/login" className="ml-1 text-blue-600 hover:underline dark:text-blue-400">
                  立即登录
                </Link>
              </div>
            </CardFooter>
          </form>
        </Card>
      </div>

      {/* 备案信息 */}
      <footer className="py-4 text-center text-sm text-gray-500 dark:text-gray-400 space-y-1">
        <p>网站域名：yikaihui.com</p>
        <p>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            京ICP备17037227号-1
          </a>
        </p>
      </footer>
    </div>
  );
}
